import React, { useState } from 'react';
import { 
  FaExclamationTriangle, FaClock, FaCalendarAlt, FaChartLine, 
  FaPercentage, FaShieldAlt, FaArrowDown, FaArrowUp, FaArrowRight, FaCalendarCheck,
  FaCalendarDay, FaCheckCircle, FaFilter, FaBolt, FaLayerGroup, FaTachometerAlt,
  FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import Last3MonthsEarningsCard from './Last3MonthsEarningsCard';
import { 
  sortBillsByDueDate,
  parseDateParts,
  getYearMonth,
  formatMonthYear,
  getTodayDateStr,
  getStreamMonthData 
} from './billTrackerData';

const DashboardSheet = ({ 
  expenses, 
  incomeStreams, 
  config, 
  setConfig,
  onNavigateToTab, 
  onOpenTuesdayPlan 
}) => {
  // Parse reference date parts safely without UTC offset shift
  const { year: refYear, month: refMonth, day: refDay } = parseDateParts(config?.referenceDate || getTodayDateStr());
  const refDate = new Date(refYear, refMonth - 1, refDay);
  const activeYearMonth = getYearMonth(config?.referenceDate || getTodayDateStr());
  const activeMonthLabel = formatMonthYear(activeYearMonth);
  const isViewingToday = (config?.referenceDate || getTodayDateStr()) === getTodayDateStr();

  // Shift Reference Month
  const handleShiftMonth = (offset) => {
    const nextDateObj = new Date(refYear, refMonth - 1 + offset, 1);
    const nextYear = nextDateObj.getFullYear();
    const nextMonth = String(nextDateObj.getMonth() + 1).padStart(2, '0');
    const newRefDate = `${nextYear}-${nextMonth}-01`;
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: newRefDate,
        autoSyncRealDate: false
      }));
    }
  };

  const handleSelectMonth = (monthKey) => {
    const [y, m] = monthKey.split('-').map(Number);
    const newRefDate = `${y}-${String(m).padStart(2, '0')}-01`;
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: newRefDate,
        autoSyncRealDate: false
      }));
    }
  };

  const handleResetToToday = () => {
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: getTodayDateStr(),
        autoSyncRealDate: true
      }));
    }
  };

  // Next Monday calculation:
  // dayOfWeek: 0 = Sun, 1 = Mon, 2 = Tue, 3 = Wed, 4 = Thu, 5 = Fri, 6 = Sat
  const dayOfWeek = refDate.getDay();
  // Days until the next Monday (if today is Monday, next Monday is 7 days away; if Sunday, 1 day away)
  const daysUntilNextMonday = ((1 - dayOfWeek + 6) % 7) + 1;
  const nextMondayDate = new Date(refYear, refMonth - 1, refDay + daysUntilNextMonday);
  const nextMondayStr = [
    nextMondayDate.getFullYear(),
    String(nextMondayDate.getMonth() + 1).padStart(2, '0'),
    String(nextMondayDate.getDate()).padStart(2, '0')
  ].join('-');
  const nextMondayFormatted = nextMondayDate.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const nextMondayShort = nextMondayDate.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  // End of current month date object
  const endOfMonth = new Date(refYear, refMonth + 1, 0);
  const endOfYear = new Date(refYear, 11, 31);
  const endOfMonthStr = [
    endOfMonth.getFullYear(),
    String(endOfMonth.getMonth() + 1).padStart(2, '0'),
    String(endOfMonth.getDate()).padStart(2, '0')
  ].join('-');

  // Filter unpaid expenses arranged strictly in order of due date
  const unpaidBills = sortBillsByDueDate(expenses.filter(b => !b.isPaid));

  // 1. Total Overdue Bills (due date < refDate and unpaid)
  const overdueBills = sortBillsByDueDate(unpaidBills.filter(b => b.dueDate < config.referenceDate));
  const totalOverdue = overdueBills.reduce((sum, b) => sum + b.amount, 0);

  // 2. Debt Due Next Monday (unpaid bills due <= nextMondayStr, arranged in due date order)
  // Reflects all debt obligations due on or before next Monday
  const debtDueNextMondayBills = sortBillsByDueDate(unpaidBills.filter(b => b.dueDate <= nextMondayStr));
  const totalDebtDueNextMonday = debtDueNextMondayBills.reduce((sum, b) => sum + b.amount, 0);

  // Portion due strictly between reference date and next Monday
  const upcomingThisWeekBills = sortBillsByDueDate(unpaidBills.filter(b => b.dueDate >= config.referenceDate && b.dueDate <= nextMondayStr));
  const totalUpcomingThisWeek = upcomingThisWeekBills.reduce((sum, b) => sum + b.amount, 0);

  // Expected Next Monday income inflow from income streams
  const expectedNextMondayIncome = incomeStreams.reduce((sum, s) => {
    if (s.mondays && Array.isArray(s.mondays)) {
      const match = s.mondays.find(m => m.date === nextMondayStr);
      if (match) return sum + (match.expected || 0);
    }
    // Fallback: prorated weekly income
    return sum + (s.expectedAmount > 0 ? (s.expectedAmount / 4.33) : 0);
  }, 0);

  // Net Monday liquidity buffer
  const nextMondayLiquidityBuffer = expectedNextMondayIncome - totalDebtDueNextMonday;

  // 3. Debt Due End of Month (unpaid bills due <= endOfMonth)
  const debtEndOfMonthBills = unpaidBills.filter(b => b.dueDate <= endOfMonthStr);
  const totalDebtEndOfMonth = debtEndOfMonthBills.reduce((sum, b) => sum + b.amount, 0);

  // 4. Debt Due End of Year (unpaid bills due in this calendar year)
  const debtEndOfYearBills = unpaidBills.filter(b => {
    const d = new Date(b.dueDate);
    return d <= endOfYear;
  });
  const totalDebtEndOfYear = debtEndOfYearBills.reduce((sum, b) => sum + b.amount, 0);

  // Total Incomes for active month
  const totalMonthlyExpectedIncome = incomeStreams.reduce((sum, s) => {
    const d = getStreamMonthData(s, activeYearMonth);
    return sum + (d.expectedAmount || 0);
  }, 0);
  const totalMonthlyActualIncome = incomeStreams.reduce((sum, s) => {
    const d = getStreamMonthData(s, activeYearMonth);
    return sum + (d.actualReceived || 0);
  }, 0);

  // Net Cashflow calculations
  // Month-end net cashflow = Monthly actual (or expected) income - Month obligations
  const monthEndNetCashflow = totalMonthlyActualIncome - totalDebtEndOfMonth;
  
  // Year-end annualized net cashflow = (Annualized income) - (Annualized obligations)
  const annualizedIncome = totalMonthlyActualIncome * 12;
  const yearEndNetCashflow = annualizedIncome - (totalDebtEndOfYear * (12 / (refMonth || 1)));

  // Safe Debt Capacity sizing algorithm:
  // Monthly ceiling = Monthly expected income * safeDebtRatio (e.g. 30%)
  const monthlyCeiling = totalMonthlyExpectedIncome * config.safeDebtRatio;
  // Remaining headroom for 30 days = monthlyCeiling - totalDebtEndOfMonth
  const monthlyHeadroom = monthlyCeiling - totalDebtEndOfMonth;
  const safeDebtMonthCapacity = Math.max(0, monthlyHeadroom);

  // 6-Month capacity: safe net cashflow over 6 months
  const safeDebt6Month = Math.max(0, Math.round((monthlyCeiling * 6) - (totalDebtEndOfMonth * 2.2)));
  // 1-Year capacity: safe net borrowing limit over 12 months
  const safeDebt1Year = Math.max(0, Math.round((monthlyCeiling * 12) - (totalDebtEndOfYear * 1.05)));

  // 48-Hour Proximity Warning: Unpaid bills due within 48h (today, tomorrow, or in 2 days)
  const urgent48hBills = unpaidBills.filter(b => {
    const [bY, bM, bD] = b.dueDate.split('-').map(Number);
    const bDate = new Date(bY, bM - 1, bD);
    const diffDays = Math.round((bDate - refDate) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 2;
  });
  const totalUrgent48h = urgent48hBills.reduce((sum, b) => sum + b.amount, 0);

  // Month-level bills (all bills scheduled in this active reference month)
  const monthAllBills = expenses.filter(b => {
    const d = new Date(b.dueDate);
    return d.getFullYear() === refYear && d.getMonth() === refMonth;
  });
  const totalMonthCommittedSpend = monthAllBills.reduce((sum, b) => sum + b.amount, 0);

  // Liquidity Runway & Cash Burn calculations
  const weeklyCashBurn = totalMonthCommittedSpend / 4.33;
  const weeklyIncomeInflow = totalMonthlyActualIncome / 4.33;
  const netWeeklyCashflow = weeklyIncomeInflow - weeklyCashBurn;
  const isCashSurplus = netWeeklyCashflow >= 0;
  const weeklyDeficit = Math.abs(netWeeklyCashflow);
  const burnToIncomeRatio = weeklyIncomeInflow > 0
    ? Math.min(100, Math.round((weeklyCashBurn / weeklyIncomeInflow) * 100))
    : 100;
  const runwayWeeksEstimate = !isCashSurplus && weeklyDeficit > 0
    ? Math.max(0, safeDebtMonthCapacity / weeklyDeficit).toFixed(1)
    : null;

  // Category Budget Ceilings & Variance
  const categoryBudgets = config.categoryBudgets || {
    Business: 12000,
    Household: 5000,
    Others: 2000
  };

  const categoryMetrics = ['Business', 'Household', 'Others'].map(cat => {
    const budget = categoryBudgets[cat] || (cat === 'Business' ? 12000 : cat === 'Household' ? 5000 : 2000);
    const spent = monthAllBills.filter(b => b.category === cat).reduce((sum, b) => sum + b.amount, 0);
    const remaining = budget - spent;
    const percentUsed = budget > 0 ? Math.round((spent / budget) * 100) : 0;
    return {
      category: cat,
      budget,
      spent,
      remaining,
      percentUsed,
      isOverBudget: remaining < 0
    };
  });

  // Format currency helper
  const fmt = (val) => {
    const isNeg = val < 0;
    const abs = Math.abs(val);
    const str = abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return isNeg ? `-$${str}` : `$${str}`;
  };

  // State to toggle itemized preview for next Monday obligations
  const [showNextMondayList, setShowNextMondayList] = useState(true);

  return (
    <div>
      {/* Month Navigation Toolbar for Dashboard */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '10px 16px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="bt-btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleShiftMonth(-1)}
            title="View previous month dashboard"
          >
            <FaChevronLeft size={10} /> Prev Month
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px' }}>
            <FaCalendarAlt style={{ color: '#2563eb' }} />
            <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{activeMonthLabel}</strong>
            <span style={{
              fontSize: '0.75rem',
              backgroundColor: isViewingToday ? '#d1fae5' : '#f1f5f9',
              color: isViewingToday ? '#065f46' : '#475569',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '6px'
            }}>
              {isViewingToday ? 'Today (Active)' : 'Viewing History'}
            </span>
          </div>

          <button
            className="bt-btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleShiftMonth(1)}
            title="Advance to next month"
          >
            Next Month <FaChevronRight size={10} />
          </button>

          {!isViewingToday && (
            <button
              className="bt-btn-secondary"
              style={{ padding: '6px 10px', fontSize: '0.8rem', color: '#059669', borderColor: '#a7f3d0', fontWeight: 600 }}
              onClick={handleResetToToday}
              title="Return to today's real calendar date"
            >
              Today
            </button>
          )}
        </div>

        <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
          Active Cashflow Horizon: <strong style={{ color: '#0f172a' }}>{activeMonthLabel}</strong>
        </div>
      </div>

      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h2 style={{
            margin: 0,
            fontSize: '0.95rem',
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#475569'
          }}>
            SUMMARY KPI DASHBOARD (SHEET 1, ROWS 6-14)
          </h2>
          <span style={{
            background: '#eff6ff',
            color: '#2563eb',
            fontSize: '0.78rem',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '6px'
          }}>
            9 Multi-Horizon Indicators
          </span>
        </div>
        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
          Real-time formula evaluation synced with Expense ledger
        </div>
      </div>

      {/* 48-Hour Urgent Proximity Warning Banner */}
      {urgent48hBills.length > 0 && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #f87171',
          borderRadius: '14px',
          padding: '14px 18px',
          marginBottom: '22px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          boxShadow: '0 4px 14px rgba(239, 68, 68, 0.12)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px'
            }}>
              <FaBolt className="bt-pulse-urgent" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.96rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>48-HOUR PAYMENT PROXIMITY ALERT</span>
                <span style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}>
                  {urgent48hBills.length} {urgent48hBills.length === 1 ? 'BILL' : 'BILLS'} DUE SOON
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#b91c1c', marginTop: '3px' }}>
                Total <strong>{fmt(totalUrgent48h)}</strong> is due today or tomorrow. Ensure settlement to maintain good supplier standing.
              </div>
            </div>
          </div>
          <button
            className="bt-btn-primary"
            style={{
              backgroundColor: '#dc2626',
              borderColor: '#b91c1c',
              padding: '8px 16px',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={() => onNavigateToTab('expenses')}
          >
            Review Urgent Bills &rarr;
          </button>
        </div>
      )}

      {/* 9 Core KPI Cards Grid */}
      <div className="bt-kpi-grid">
        {/* KPI 1: Total Overdue Bills */}
        <div className="bt-kpi-card" style={{ borderColor: totalOverdue > 0 ? '#fecaca' : '#e2e8f0' }}>
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">TOTAL OVERDUE BILLS</span>
            <div className="bt-kpi-icon danger">
              <FaExclamationTriangle />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value danger">{fmt(totalOverdue)}</div>
            <div className="bt-kpi-sub" style={{ color: totalOverdue > 0 ? '#dc2626' : '#64748b' }}>
              <FaExclamationTriangle size={12} />
              {overdueBills.length} unpaid bill{overdueBills.length === 1 ? '' : 's'} past due
            </div>
          </div>
        </div>

        {/* KPI 2: Debt Due Next Monday (NEW) */}
        <div 
          className="bt-kpi-card" 
          style={{ 
            borderColor: totalDebtDueNextMonday > 0 ? '#fed7aa' : '#e2e8f0',
            backgroundColor: totalDebtDueNextMonday > 0 ? '#fffbf5' : '#ffffff',
            cursor: 'pointer'
          }}
          onClick={() => setShowNextMondayList(prev => !prev)}
          title="Click to toggle itemized list of obligations due by next Monday"
        >
          <div className="bt-kpi-header">
            <span className="bt-kpi-title" style={{ color: '#c2410c' }}>DEBT DUE NEXT MONDAY</span>
            <div className="bt-kpi-icon" style={{ background: '#fff7ed', color: '#ea580c' }}>
              <FaCalendarDay />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value" style={{ color: totalDebtDueNextMonday > 0 ? '#ea580c' : '#0f172a' }}>
              {fmt(totalDebtDueNextMonday)}
            </div>
            <div className="bt-kpi-sub" style={{ color: '#64748b' }}>
              <FaClock size={12} style={{ color: '#ea580c' }} />
              Due by {nextMondayShort} ({debtDueNextMondayBills.length} bill{debtDueNextMondayBills.length === 1 ? '' : 's'})
            </div>
          </div>
        </div>

        {/* KPI 3: Debt Due End of Month */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">DEBT DUE END OF MONTH</span>
            <div className="bt-kpi-icon warning">
              <FaClock />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value">{fmt(totalDebtEndOfMonth)}</div>
            <div className="bt-kpi-sub">
              Unpaid bills due &le; {endOfMonthStr}
            </div>
          </div>
        </div>

        {/* KPI 4: Debt Due End of Year */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">DEBT DUE END OF YEAR</span>
            <div className="bt-kpi-icon purple">
              <FaCalendarAlt />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value">{fmt(totalDebtEndOfYear)}</div>
            <div className="bt-kpi-sub">
              All unpaid obligations this calendar year
            </div>
          </div>
        </div>

        {/* KPI 4: Month-End Net Cashflow */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">MONTH-END NET CASHFLOW</span>
            <div className={`bt-kpi-icon ${monthEndNetCashflow < 0 ? 'danger' : 'success'}`}>
              {monthEndNetCashflow < 0 ? <FaArrowDown /> : <FaArrowUp />}
            </div>
          </div>
          <div>
            <div className={`bt-kpi-value ${monthEndNetCashflow < 0 ? 'negative' : 'positive'}`}>
              {fmt(monthEndNetCashflow)}
            </div>
            <div className="bt-kpi-sub">
              Monthly Income minus current obligations
            </div>
          </div>
        </div>

        {/* KPI 5: Year-End Net Cashflow */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">YEAR-END NET CASHFLOW</span>
            <div className={`bt-kpi-icon ${yearEndNetCashflow < 0 ? 'danger' : 'success'}`}>
              <FaChartLine />
            </div>
          </div>
          <div>
            <div className={`bt-kpi-value ${yearEndNetCashflow < 0 ? 'negative' : 'positive'}`}>
              {fmt(yearEndNetCashflow)}
            </div>
            <div className="bt-kpi-sub">
              Annualized run-rate cashflow
            </div>
          </div>
        </div>

        {/* KPI 6: Debt Capacity (Month) */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">DEBT CAPACITY (MONTH)</span>
            <div className="bt-kpi-icon info">
              <FaShieldAlt />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value">{fmt(safeDebtMonthCapacity)}</div>
            <div className="bt-kpi-sub">
              Max safe debt ceiling for 30 days ({Math.round(config.safeDebtRatio * 100)}% cap)
            </div>
          </div>
        </div>

        {/* KPI 7: Debt Capacity (6 Mo) */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">DEBT CAPACITY (6 MO)</span>
            <div className="bt-kpi-icon info">
              <FaPercentage />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value">{fmt(safeDebt6Month)}</div>
            <div className="bt-kpi-sub">
              Safe borrowing limit over 6 months
            </div>
          </div>
        </div>

        {/* KPI 8: Debt Capacity (1 Year) */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">DEBT CAPACITY (1 YEAR)</span>
            <div className="bt-kpi-icon success">
              <FaShieldAlt />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value">{fmt(safeDebt1Year)}</div>
            <div className="bt-kpi-sub">
              Safe borrowing limit over 12 months
            </div>
          </div>
        </div>
      </div>

      {/* 3-Month Earnings Activity Comparison Card */}
      <Last3MonthsEarningsCard
        incomeStreams={incomeStreams}
        referenceDate={config?.referenceDate}
        onSelectMonth={handleSelectMonth}
      />

      {/* Two Column Layout: Monthly Cashflow Summary & Safe Borrowing Limits */}
      <div className="bt-two-col">
        {/* Left: Monthly Cashflow Summary Table */}
        <div className="bt-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                MONTHLY CASHFLOW SUMMARY &bull; {activeMonthLabel}
              </h3>
              <div className="bt-subtext">Expected commitments vs actual receipts for {activeMonthLabel}</div>
            </div>
            <button
              className="bt-badge"
              style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                color: '#4f46e5',
                cursor: 'pointer',
                fontWeight: 700
              }}
              onClick={() => onNavigateToTab('income')}
            >
              Manage Streams <FaArrowRight />
            </button>
          </div>

          <div className="bt-table-container">
            <table className="bt-table">
              <thead>
                <tr className="bt-table-header">
                  <th>CASHFLOW STREAM</th>
                  <th style={{ textAlign: 'right' }}>MONTHLY EXPECTED ($)</th>
                  <th style={{ textAlign: 'right' }}>ACTUAL RECEIVED ($)</th>
                  <th style={{ width: '180px' }}>FULFILLMENT</th>
                </tr>
              </thead>
              <tbody>
                {incomeStreams.map((stream) => {
                  const monthData = getStreamMonthData(stream, activeYearMonth);
                  const expected = monthData.expectedAmount;
                  const actual = monthData.actualReceived;
                  const rate = expected > 0
                    ? Math.min(100, Math.round((actual / expected) * 100))
                    : 0;

                  return (
                    <tr key={stream.id} className="bt-table-row">
                      <td className="bt-table-cell">
                        <div className="bt-title-cell">{stream.name}</div>
                        {stream.description && (
                          <div className="bt-subtext">{stream.description}</div>
                        )}
                      </td>
                      <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 600 }}>
                        ${expected.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 700, color: actual > 0 ? '#059669' : '#64748b' }}>
                        ${actual.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        {actual === 0 && (
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>Refreshed ($0.00)</div>
                        )}
                      </td>
                      <td className="bt-table-cell">
                        <div className="bt-progress-wrapper">
                          <div className="bt-progress-track">
                            <div
                              className={`bt-progress-fill ${rate >= 100 ? 'green' : (rate > 0 ? 'purple' : 'gray')}`}
                              style={{ width: `${rate}%` }}
                            />
                          </div>
                          <span className="bt-progress-text">{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Safe Borrowing Limits Block */}
        <div className="bt-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                SAFE BORROWING LIMITS
              </h3>
              <span style={{
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                padding: '2px 8px',
                borderRadius: '6px',
                fontSize: '0.78rem',
                fontWeight: 700
              }}>
                Cap: {Math.round(config.safeDebtRatio * 100)}%
              </span>
            </div>
            <p className="bt-subtext" style={{ marginBottom: '20px' }}>
              Dynamic debt capacity based on safe cashflow debt ceiling minus active obligations.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.9rem', color: '#475569' }}>Current Month Ceiling:</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                  ${monthlyCeiling.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.9rem', color: '#475569' }}>Month Unpaid Debt:</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#dc2626' }}>
                  ${totalDebtEndOfMonth.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.9rem', color: '#ea580c', fontWeight: 600 }}>Debt Due Next Monday ({nextMondayShort}):</span>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: '#ea580c' }}>
                  ${totalDebtDueNextMonday.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.9rem', color: '#475569' }}>Expected Monday Inflow:</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>
                  +${expectedNextMondayIncome.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.9rem', color: '#475569' }}>Monday Net Buffer:</span>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: nextMondayLiquidityBuffer >= 0 ? '#16a34a' : '#dc2626' }}>
                  {fmt(nextMondayLiquidityBuffer)}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 14px',
                backgroundColor: monthlyHeadroom < 0 ? '#fef2f2' : '#f0fdf4',
                borderRadius: '10px',
                border: `1px solid ${monthlyHeadroom < 0 ? '#fecaca' : '#bbf7d0'}`
              }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: monthlyHeadroom < 0 ? '#b91c1c' : '#15803d' }}>
                  Remaining 30d Headroom:
                </span>
                <span style={{ fontWeight: 800, fontSize: '1.15rem', color: monthlyHeadroom < 0 ? '#dc2626' : '#16a34a' }}>
                  {fmt(monthlyHeadroom)}
                </span>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '20px',
            padding: '12px 14px',
            backgroundColor: '#f8fafc',
            borderRadius: '10px',
            border: '1px dashed #cbd5e1',
            fontSize: '0.8rem',
            color: '#64748b'
          }}>
            {monthlyHeadroom < 0 ? (
              <span>
                <strong>Warning:</strong> Current commitments exceed the {Math.round(config.safeDebtRatio * 100)}% safe borrowing ceiling. Defer discretionary expenses or request invoice extensions.
              </span>
            ) : (
              <span>
                <strong>Optimal State:</strong> Healthy cash headroom available. You can safely add up to <strong>{fmt(safeDebtMonthCapacity)}</strong> in new commitments this month.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Category Budget Ceilings & Liquidity Runway Two-Column Grid */}
      <div className="bt-two-col" style={{ marginTop: '20px' }}>
        {/* Left: Category Budget Ceilings & Variance Meters */}
        <div className="bt-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaLayerGroup style={{ color: '#4f46e5' }} />
                CATEGORY BUDGET CEILINGS & VARIANCE
              </h3>
              <div className="bt-subtext">Active month spend commitments vs allocated budget targets</div>
            </div>
            <button
              className="bt-badge"
              style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                color: '#4f46e5',
                cursor: 'pointer',
                fontWeight: 700
              }}
              onClick={() => onNavigateToTab('expenses')}
            >
              Manage Ledger &rarr;
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {categoryMetrics.map(item => {
              const barColor = item.percentUsed > 90 ? '#ef4444' : item.percentUsed > 70 ? '#f59e0b' : '#10b981';
              return (
                <div key={item.category} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>{item.category}</strong>
                      <span className="bt-badge" style={{ fontSize: '0.72rem', background: '#f1f5f9', color: '#475569' }}>
                        Cap: {fmt(item.budget)}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 800, fontSize: '0.95rem', color: item.isOverBudget ? '#dc2626' : '#0f172a' }}>
                        {fmt(item.spent)}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '6px' }}>
                        ({item.percentUsed}%)
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(100, item.percentUsed)}%`,
                      backgroundColor: barColor,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '0.76rem' }}>
                    <span style={{ color: '#64748b' }}>
                      {item.isOverBudget ? 'Over Budget:' : 'Remaining Headroom:'}
                    </span>
                    <span style={{ fontWeight: 700, color: item.isOverBudget ? '#dc2626' : '#16a34a' }}>
                      {item.isOverBudget ? `-${fmt(Math.abs(item.remaining))} EXCEEDED` : `+${fmt(item.remaining)} buffer`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Liquidity Runway & Cash Burn Gauge */}
        <div className="bt-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaTachometerAlt style={{ color: isCashSurplus ? '#10b981' : '#f59e0b' }} />
                  LIQUIDITY RUNWAY & CASH BURN GAUGE
                </h3>
                <div className="bt-subtext">Operational velocity and net cash sustainability</div>
              </div>
              <span style={{
                background: isCashSurplus ? '#ecfdf5' : '#fef2f2',
                border: `1px solid ${isCashSurplus ? '#a7f3d0' : '#fecaca'}`,
                color: isCashSurplus ? '#047857' : '#b91c1c',
                padding: '2px 8px',
                borderRadius: '6px',
                fontSize: '0.78rem',
                fontWeight: 700
              }}>
                {isCashSurplus ? '🟢 Self-Sustaining' : '🔴 Burn Watch'}
              </span>
            </div>

            {/* Velocity Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Weekly Burn</div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#dc2626', marginTop: '2px' }}>
                  -{fmt(weeklyCashBurn)}
                </div>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{fmt(totalMonthCommittedSpend)}/mo</div>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Weekly Inflow</div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>
                  +{fmt(weeklyIncomeInflow)}
                </div>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{fmt(totalMonthlyActualIncome)}/mo</div>
              </div>

              <div style={{
                background: isCashSurplus ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${isCashSurplus ? '#bbf7d0' : '#fecaca'}`,
                borderRadius: '10px',
                padding: '10px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.72rem', color: isCashSurplus ? '#166534' : '#991b1b', fontWeight: 700, textTransform: 'uppercase' }}>Net Weekly</div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: isCashSurplus ? '#15803d' : '#dc2626', marginTop: '2px' }}>
                  {isCashSurplus ? `+${fmt(netWeeklyCashflow)}` : fmt(netWeeklyCashflow)}
                </div>
                <div style={{ fontSize: '0.68rem', color: isCashSurplus ? '#15803d' : '#b91c1c' }}>
                  {isCashSurplus ? 'Surplus' : 'Deficit'}
                </div>
              </div>
            </div>

            {/* Burn to Income Meter */}
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 700, marginBottom: '6px' }}>
                <span style={{ color: '#475569' }}>Spend-to-Inflow Burn Ratio</span>
                <span style={{ color: burnToIncomeRatio > 90 ? '#dc2626' : burnToIncomeRatio > 70 ? '#d97706' : '#16a34a' }}>
                  {burnToIncomeRatio}%
                </span>
              </div>
              <div style={{ height: '10px', backgroundColor: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, burnToIncomeRatio)}%`,
                  backgroundColor: burnToIncomeRatio > 90 ? '#ef4444' : burnToIncomeRatio > 70 ? '#f59e0b' : '#10b981',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          </div>

          {/* Runway Summary Box */}
          <div style={{
            padding: '12px 14px',
            backgroundColor: isCashSurplus ? '#f0fdf4' : '#fffbeb',
            borderRadius: '10px',
            border: `1px solid ${isCashSurplus ? '#bbf7d0' : '#fde68a'}`,
            fontSize: '0.82rem',
            color: isCashSurplus ? '#14532d' : '#92400e'
          }}>
            {isCashSurplus ? (
              <div>
                <strong>Self-Sustaining Operations:</strong> Revenue inflow exceeds monthly commitments by <strong>{fmt(monthEndNetCashflow)}</strong>. Zero reliance on borrowing; excess liquidity is accumulating into reserves.
              </div>
            ) : (
              <div>
                <strong>Cash Burn Alert:</strong> Net weekly deficit of <strong>-{fmt(weeklyDeficit)}/wk</strong>. Based on your 30-day borrowing headroom of {fmt(safeDebtMonthCapacity)}, you have approximately <strong>{runwayWeeksEstimate} weeks</strong> of operational runway.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Itemized Debt Due Next Monday Breakdown */}
      {showNextMondayList && (
        <div className="bt-card" style={{ marginTop: '20px', borderLeft: '4px solid #ea580c' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', color: '#0f172a' }}>
                  OBLIGATIONS DUE BY NEXT MONDAY ({nextMondayFormatted})
                </h3>
                <span style={{
                  background: '#fff7ed',
                  color: '#ea580c',
                  border: '1px solid #fed7aa',
                  borderRadius: '6px',
                  padding: '2px 8px',
                  fontSize: '0.78rem',
                  fontWeight: 700
                }}>
                  {debtDueNextMondayBills.length} {debtDueNextMondayBills.length === 1 ? 'Bill' : 'Bills'} Due
                </span>
              </div>
              <div className="bt-subtext" style={{ marginTop: '4px' }}>
                Total settlement required by next Monday: <strong style={{ color: '#ea580c' }}>{fmt(totalDebtDueNextMonday)}</strong>
                {totalOverdue > 0 && <span style={{ color: '#dc2626' }}> (includes {fmt(totalOverdue)} overdue)</span>}
                {totalUpcomingThisWeek > 0 && <span style={{ color: '#2563eb' }}> &bull; {fmt(totalUpcomingThisWeek)} scheduled this week</span>}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                className="bt-badge"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  color: '#ea580c',
                  cursor: 'pointer',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => onNavigateToTab('expenses')}
                title="Manage and pay these bills in the expenses ledger"
              >
                <FaFilter size={10} /> Open in Expenses Ledger <FaArrowRight size={10} />
              </button>
            </div>
          </div>

          {debtDueNextMondayBills.length === 0 ? (
            <div style={{
              padding: '20px',
              backgroundColor: '#f0fdf4',
              border: '1px dashed #86efac',
              borderRadius: '10px',
              color: '#15803d',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.9rem'
            }}>
              <FaCheckCircle size={18} />
              <span>
                <strong>Clear Schedule!</strong> There are no unpaid bills or overdue obligations due on or before next Monday ({nextMondayFormatted}).
              </span>
            </div>
          ) : (
            <div className="bt-table-container">
              <table className="bt-table">
                <thead>
                  <tr className="bt-table-header">
                    <th>BILL DESCRIPTION</th>
                    <th>CATEGORY</th>
                    <th>DUE DATE</th>
                    <th>TIMELINE STATUS</th>
                    <th style={{ textAlign: 'right' }}>AMOUNT ($)</th>
                    <th style={{ textAlign: 'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {debtDueNextMondayBills.map((bill) => {
                    const isOverdue = bill.dueDate < config.referenceDate;
                    const isDueOnMonday = bill.dueDate === nextMondayStr;

                    return (
                      <tr key={bill.id} className="bt-table-row">
                        <td className="bt-table-cell">
                          <div className="bt-title-cell">{bill.description}</div>
                          {bill.subDescription && (
                            <div className="bt-subtext">{bill.subDescription}</div>
                          )}
                        </td>
                        <td className="bt-table-cell">
                          <span className="bt-badge bt-badge-category">{bill.category}</span>
                        </td>
                        <td className="bt-table-cell" style={{ fontWeight: 600 }}>
                          {bill.dueDate}
                        </td>
                        <td className="bt-table-cell">
                          {isOverdue ? (
                            <span className="bt-badge bt-badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <FaExclamationTriangle size={10} /> Overdue (Past Due)
                            </span>
                          ) : isDueOnMonday ? (
                            <span className="bt-badge" style={{ backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <FaClock size={10} /> Due on Next Monday
                            </span>
                          ) : (
                            <span className="bt-badge" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <FaCalendarDay size={10} /> Due This Week
                            </span>
                          )}
                        </td>
                        <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.98rem', color: isOverdue ? '#dc2626' : '#0f172a' }}>
                          ${bill.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                          {bill.hasThreeWeekPlan ? (
                            <button
                              className="bt-btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '0.78rem' }}
                              onClick={() => onOpenTuesdayPlan(bill)}
                            >
                              Tuesday Plan
                            </button>
                          ) : (
                            <button
                              className="bt-btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '0.78rem' }}
                              onClick={() => onNavigateToTab('expenses')}
                            >
                              Ledger
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Quick 3-Week Tuesday Plan Summary Block */}
      <div className="bt-card" style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalendarCheck style={{ color: '#6366f1' }} />
              ACTIVE 3-WEEK TUESDAY INSTALLMENT COMMITMENTS
            </h3>
            <div className="bt-subtext">Important invoices scheduled across consecutive Tuesdays</div>
          </div>
          <button
            className="bt-badge"
            style={{
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              color: '#4f46e5',
              cursor: 'pointer',
              fontWeight: 700
            }}
            onClick={() => onNavigateToTab('expenses')}
          >
            View All Bills <FaArrowRight />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
          {sortBillsByDueDate(expenses.filter(b => b.hasThreeWeekPlan && !b.isPaid))
            .slice(0, 3)
            .map(bill => (
              <div
                key={bill.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{bill.description}</span>
                    <span className="bt-badge bt-badge-important" style={{ fontSize: '0.72rem' }}>
                      ${bill.amount.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="bt-subtext">{bill.subDescription}</div>
                </div>

                <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    3 Weekly Tranches: ~${Math.round(bill.amount / 3).toLocaleString()}/Tue
                  </span>
                  <button
                    className="bt-btn-secondary"
                    style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                    onClick={() => onOpenTuesdayPlan(bill)}
                  >
                    Open Plan
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardSheet;
